import { useCallback, useState } from 'react';
import { useStore } from 'hooks/useStore';
import { toast } from 'react-toastify';
import { oocService } from 'services/fdp/oocService';
import { FCColorType, OOCForecastCell } from '../oocForecast.type';
import { periodToLabel } from 'data/fdp/oocMockData';

export { periodToLabel };

export const getCellClass = (colorType: FCColorType, styles: Record<string, string>): string => {
  switch (colorType) {
    case 'new':
      return styles.cellNew;
    case 'modified':
      return styles.cellModified;
    case 'removed':
      return styles.cellRemoved;
    default:
      return styles.cellUnchanged;
  }
};

export const formatCellValue = (cell: OOCForecastCell): string => {
  if (cell.value === null || cell.value === undefined) return '-';
  return cell.value.toLocaleString('en-US');
};

export const useOOCCommunicateLogic = () => {
  const { OOCStore } = useStore();
  const { currentSubmission, navigateToDashboard, markAsCommunicated, sendEmail, isSendingEmail } = OOCStore;

  const [isExportingExcel, setIsExportingExcel] = useState(false);

  const handleExportExcel = useCallback(async () => {
    if (!currentSubmission) return;
    setIsExportingExcel(true);
    try {
      const blob = await oocService.exportExcel(currentSubmission.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${currentSubmission.id}_OOC_Forecast.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error("We couldn't export this submission to Excel. Please try again.");
    } finally {
      setIsExportingExcel(false);
    }
  }, [currentSubmission]);

  const handleSendEmail = useCallback(() => {
    sendEmail();
  }, [sendEmail]);

  const handleMarkCommunicated = useCallback(() => {
    markAsCommunicated();
  }, [markAsCommunicated]);

  const emailData = currentSubmission
    ? (() => {
        const { emailPreview, adjustments, id } = currentSubmission;
        return {
          masAdj: emailPreview?.masAdjustments ?? adjustments.filter((a) => a.tab === 'MAS'),
          ncAdj: emailPreview?.ncAdjustments ?? adjustments.filter((a) => a.tab === 'NC'),
          provMasAdj: emailPreview?.provMasAdjustments ?? adjustments.filter((a) => a.tab === 'ProvMAS'),
          provNcAdj: emailPreview?.provNcAdjustments ?? adjustments.filter((a) => a.tab === 'ProvNC'),
          toField: emailPreview?.to ?? 'SC, Purchasing, PPS, Business Development, CSE',
          subjectField: emailPreview?.subject ?? `OOC Forecast Update - ${id}`,
          attachmentField: emailPreview?.attachment ?? `${id}_Forecast_Adjustments.xlsx`,
          greetingText:
            emailPreview?.bodyGreeting ??
            `Hello Team,\n\nPlease find below the out-of-cycle forecast adjustments for ${id}. The following changes have been communicated and are now under Supply Chain review.`,
          signatureText:
            emailPreview?.bodySignature ?? `Best regards,\nDemand Planning Team`,
        };
      })()
    : null;

  return {
    currentSubmission,
    navigateToDashboard,
    handleExportExcel,
    handleSendEmail,
    handleMarkCommunicated,
    isSendingEmail,
    isExportingExcel,
    emailData,
  };
};
